import { useEffect, useRef, useState } from 'react'
import { Button } from './ui.tsx'

/**
 * Cuadro para pedir un texto (motivo, alegación, resolución).
 *
 * Sustituye a `window.prompt`, que en iOS recorta los textos largos, no se deja
 * estilar y bloquea la página entera. Aquí además se puede escribir en varias
 * líneas, que es lo natural cuando alguien explica por qué no está de acuerdo.
 */
export function PromptDialog({
  open,
  title,
  description,
  placeholder,
  confirmLabel = 'Enviar',
  tone = 'primary',
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean
  title: string
  description?: string
  placeholder?: string
  confirmLabel?: string
  tone?: 'primary' | 'danger'
  busy?: boolean
  onCancel: () => void
  onConfirm: (text: string) => void
}) {
  const [text, setText] = useState('')
  const area = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (open) {
      setText('')
      // El foco va con un pequeño retardo: en iOS, enfocar mientras el cuadro
      // aún está apareciendo no levanta el teclado.
      const timer = setTimeout(() => area.current?.focus(), 120)
      return () => clearTimeout(timer)
    }
  }, [open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 sm:items-center">
      <div className="w-full max-w-sm rounded-2xl border border-[var(--color-line)] bg-[var(--color-surface)] p-5">
        <h2 className="text-lg font-semibold">{title}</h2>
        {description && (
          <p className="mt-1 text-sm text-[var(--color-muted)]">{description}</p>
        )}

        <textarea
          ref={area}
          rows={4}
          value={text}
          placeholder={placeholder}
          onChange={(e) => setText(e.target.value)}
          className="mt-3 w-full resize-none rounded-xl border border-[var(--color-line)] bg-[var(--color-canvas)] px-3 py-2 text-[16px]"
        />

        <div className="mt-3 flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onCancel} disabled={busy}>
            Cancelar
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            className="flex-1"
            disabled={busy || text.trim().length === 0}
            onClick={() => onConfirm(text.trim())}
          >
            {busy ? 'Enviando…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
